import {SELECTIVE_AI_HELP_LIVE, isSelectiveAiDomains} from 'common/HelperFunctions'

import {AssistantClarification} from './assistantResponseContract'

/**
 * Which part of the portal the reader is chatting from. The backend scopes answers by it
 * (`options.pageContext.surface`): on a scoped surface only admission how-tos are answered.
 * - `admission_admin` — `/institute/admission/*` for a student or parent (applicant or not).
 * - `admission_applicant` — `/admissions/*` (landing, auth, elements, dashboard, form) for anyone
 *   who is not portal staff, signed in or not; see `chatEndpointFor`.
 * - `general` — everywhere else, and everywhere for ADMIN / TEACHER / STAFF.
 */
export type ChatSurface = 'general' | 'admission_admin' | 'admission_applicant'

/**
 * Where a turn is sent. The body and the response contract are the same for all three.
 * - `chatbot` — `chatbot/chat`, portal token.
 * - `admission_applicant` — `admission/chat`, the signed-in applicant's token.
 * - `admission_public` — `admission/public-chat`, no token (signed out, or a signed-in student or
 *   parent who is not an applicant: their portal token would 401 on `admission/chat`).
 */
export type AssistantChatEndpoint = 'chatbot' | 'admission_applicant' | 'admission_public'

/** Who is looking at the page, as far as choosing a surface and an endpoint goes. */
export type ChatViewer = {
  isLoggedIn?: boolean | null
  userType?: string | null
  isAdmissionUser?: boolean | null
}

export const SIGNED_OUT_VIEWER: ChatViewer = {
  isLoggedIn: false,
  userType: null,
  isAdmissionUser: false,
}

// Case-insensitive because react-router matches routes case-insensitively.
const APPLICANT = /^\/admissions(?:\/|$)/i
const STAFF_ADMISSION = /^\/institute\/admission(?:\/|$)/i // does NOT match /institute/admission-chatbot

const PORTAL_STAFF_TYPES = new Set(['ADMIN', 'TEACHER', 'STAFF'])

/** A signed-in ADMIN, TEACHER or STAFF user: always the general assistant, on every page. */
export const isPortalStaffViewer = (viewer: ChatViewer) =>
  Boolean(viewer.isLoggedIn) && PORTAL_STAFF_TYPES.has(viewer.userType ?? '')

export function getChatSurface(pathname: string, viewer: ChatViewer): ChatSurface {
  if (isPortalStaffViewer(viewer)) return 'general'
  if (APPLICANT.test(pathname)) return 'admission_applicant'
  if (STAFF_ADMISSION.test(pathname)) return 'admission_admin'
  return 'general'
}

/** A signed-in admission applicant: a STUDENT token issued by the admission login/signup. */
export const isAdmissionApplicant = (
  userType: string | null | undefined,
  isAdmissionUser: boolean | null | undefined
) => userType === 'STUDENT' && Boolean(isAdmissionUser)

/** The surfaces the backend scopes; they never offer attendance, directory or ticket affordances. */
export const isScopedAdmissionSurface = (surface: ChatSurface) =>
  surface === 'admission_admin' || surface === 'admission_applicant'

/**
 * The endpoint a turn on this surface goes to, by who is asking: a signed-in applicant on the
 * applicant surface uses `admission/chat`; any other viewer there uses `admission/public-chat`.
 */
export function chatEndpointFor(surface: ChatSurface, viewer: ChatViewer): AssistantChatEndpoint {
  if (surface !== 'admission_applicant') return 'chatbot'
  const signedInApplicant =
    Boolean(viewer.isLoggedIn) && isAdmissionApplicant(viewer.userType, viewer.isAdmissionUser)
  return signedInApplicant ? 'admission_applicant' : 'admission_public'
}

// The admission form (`/admissions/apply`) and the applicant dashboard (`/admissions/dashboard`).
const APPLICANT_CHAT_PAGE = /^\/admissions\/(?:apply|dashboard)\/?$/i

/** Whether this is one of the applicant pages Versi may open on for tenants that allow it. */
export const isApplicantChatPath = (pathname: string) => APPLICANT_CHAT_PAGE.test(pathname)

/**
 * Whether Versi stays unmounted on this path: the legacy admission chatbot page itself, and the
 * Learning Assistant (`/learning-assistant`), a Versi chat of its own that answers from the
 * curriculum books.
 */
export const isChatbotHiddenOnPath = (pathname: string) =>
  /admission-chatbot/i.test(pathname) || /^\/learning-assistant(\/|$)/i.test(pathname)

// The onboarding wizard (`/onboarding…`), case-insensitive like the router.
const ONBOARDING = /^\/onboarding/i

/**
 * Whether App mounts Versi at all: for a signed-in user anywhere it is not hidden, and for a
 * signed-out visitor only on the applicant admission pages. On the selective AI domains Versi is
 * the main app's help bot, so it stays off the onboarding wizard (whose Teacher Allocation has an
 * assistant of its own), and off everywhere until the help bot is live (SELECTIVE_AI_HELP_LIVE).
 */
export const shouldMountChatbot = (pathname: string, viewer: ChatViewer) =>
  (Boolean(viewer.isLoggedIn) || getChatSurface(pathname, viewer) === 'admission_applicant') &&
  !isChatbotHiddenOnPath(pathname) &&
  !(isSelectiveAiDomains() && (!SELECTIVE_AI_HELP_LIVE || ONBOARDING.test(pathname)))

/** The capabilities the backend allows on the admission surface (node-api `pageScope.js`). */
const ADMISSION_CAPABILITIES = new Set(['smalltalk', 'support.manual'])

/**
 * Whether a capability id may be sent from this surface. The backend ignores anything else on a
 * scoped turn anyway; dropping it here keeps a stale chip from asking for it at all.
 */
export function isCapabilityAllowedOnSurface(
  capabilityId: string | null | undefined,
  surface: ChatSurface
): boolean {
  if (!capabilityId || !isScopedAdmissionSurface(surface)) return true
  return ADMISSION_CAPABILITIES.has(capabilityId)
}

/**
 * The clarification as it should be SHOWN on this surface.
 *
 * The backend never offers attendance or directory options on a scoped turn, but a clarification
 * already on screen from before navigating to an admission page could — and clicking one there
 * would only earn a refusal. Slot options are attendance slots (class, dates, person). Display
 * only: the stored clarification is echoed back exactly as it was received.
 */
export function filterClarificationForSurface(
  clarification: AssistantClarification | null | undefined,
  surface: ChatSurface
): AssistantClarification | null {
  if (!clarification) return null
  if (!isScopedAdmissionSurface(surface)) return clarification

  const options = clarification.options.filter((option) => {
    if (option.slot) return false
    const capabilityId = option.capabilityId ?? ''
    return !capabilityId.startsWith('attendance.') && capabilityId !== 'school.directory'
  })
  if (options.length === 0) return null
  return options.length === clarification.options.length
    ? clarification
    : {...clarification, options}
}
